import { useState, useEffect, useCallback, useRef } from 'react';
import { MessageSquare, Send, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiPost } from '@/lib/api';
import { formatDateTime, getCaseTypeLabel } from '@/lib/formatters';
import styles from './MessagesPanel.module.css';

/**
 * Conversations on one application.
 *
 * A funder sees exactly one thread — their own. The client, their legal team and
 * admins see one per funder. That asymmetry is enforced server-side; this
 * component simply renders whatever /messages/threads returns for the caller and
 * never filters by role itself, so the UI cannot disagree with the API about who
 * may see what.
 *
 * Timestamps go through formatDateTime, not `new Date()`. The API sends UTC and
 * the business runs on Eastern; parsing directly reintroduces the four-hour bug
 * fixed in lib/formatters.
 */

const ROLE_LABEL = {
  client: 'Client',
  lawyer: 'Attorney',
  paralegal: 'Paralegal',
  funder: 'Funder',
  admin: 'Admin',
};

// Who else can see this, said from the reader's own seat. One sentence for
// everyone read "The client's legal team is part of this conversation" — which
// is written from the funder's chair and lands wrong on an attorney, to whom
// "the client's legal team" is themselves.
const PRIVACY_NOTICE = {
  funder: "The client's legal team is part of this conversation. Other funders cannot see it.",
  client: 'Your legal team is part of this conversation. Other funders cannot see it.',
  lawyer: 'You, your client and this funder are in this conversation. Other funders cannot see it.',
  paralegal: 'You, the client and this funder are in this conversation. Other funders cannot see it.',
  admin: 'The client, their legal team and this funder are in this conversation. Other funders cannot see it.',
};

const DEFAULT_PRIVACY_NOTICE = 'Other funders cannot see this conversation.';

// How often an open conversation re-checks for replies. Faster than the bell's
// 30s: a thread you are looking at should not need a page refresh to move,
// which is exactly how this first shipped. Paused entirely while the tab is
// hidden.
const POLL_MS = 10000;

// Treat the reader as "at the bottom" within this many pixels. Auto-scrolling
// someone who has deliberately scrolled up to re-read history is worse than not
// scrolling at all.
const AT_BOTTOM_PX = 80;

// The fragment the notification bell links to, and the id this panel answers to.
export const SECTION_ID = 'messages';

// How many participant chips fit a header before it wraps into noise.
const ROSTER_VISIBLE = 4;

// Who survives the cap. A naive slice hid the funder — they are added last, so
// a firm with several paralegals pushed the counterparty into "+3 more". The
// client, the attorney and the funder are the three that carry meaning; support
// staff collapse first.
const ROSTER_PRIORITY = { client: 0, lawyer: 1, funder: 2, admin: 3, paralegal: 4 };

const byRosterPriority = (a, b) =>
  (ROSTER_PRIORITY[a.role] ?? 9) - (ROSTER_PRIORITY[b.role] ?? 9);

const initials = (name) =>
  (name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');

const MessagesPanel = ({
  applicationId,
  currentUser,
  canStartThread = false,
  fill = false,
  onActivity,
  // The inbox selects a conversation, not an application. Without this the
  // panel could only be pointed at an application, and on one with two funders
  // it opened its own chooser instead of what was clicked — in exactly the
  // multi-funder case the inbox exists to serve.
  initialThreadId = null,
}) => {
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [caseInfo, setCaseInfo] = useState(null);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [starting, setStarting] = useState(false);
  const listRef = useRef(null);
  const wasAtBottom = useRef(true);
  // Mirrors `messages` so the poll can compare without depending on it, which
  // would tear down and rebuild the interval on every message.
  const messagesRef = useRef([]);
  const panelRef = useRef(null);
  const broughtIntoView = useRef(false);

  const myThreads = threads.filter((t) => t.application_id === applicationId);

  const fetchThreads = useCallback(async () => {
    try {
      const all = await apiGet('/messages/threads');
      // Coerced rather than trusted: this panel renders inside three dashboards,
      // and anything other than a list here (an error envelope, an older API)
      // would otherwise crash the whole application detail view over a feature
      // that is meant to be incidental to it.
      const list = Array.isArray(all) ? all : [];
      setThreads(list);
      return list.filter((t) => t.application_id === applicationId);
    } catch (e) {
      console.error('Failed to load conversations:', e);
      return [];
    } finally {
      setLoading(false);
    }
  }, [applicationId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const mine = await fetchThreads();
      if (cancelled) return;
      // An explicitly requested conversation wins. Otherwise open the only one
      // there is — a funder always has exactly one, and making them click it is
      // friction with no decision behind it.
      const requested = initialThreadId && mine.some((t) => t.thread_id === initialThreadId)
        ? initialThreadId
        : null;
      if (requested) setActiveId(requested);
      else if (mine.length === 1) setActiveId(mine[0].thread_id);
    })();
    return () => { cancelled = true; };
  }, [fetchThreads, initialThreadId]);

  const openThread = useCallback(async (threadId) => {
    setActiveId(threadId);
    try {
      const data = await apiGet(`/messages/threads/${threadId}`);
      setMessages(data.messages || []);
      setParticipants(Array.isArray(data.participants) ? data.participants : []);
      setCaseInfo(data.application || null);
      await apiPost(`/messages/threads/${threadId}/read`, {});
      setThreads((prev) => prev.map((t) => (t.thread_id === threadId ? { ...t, unread: false } : t)));
    } catch (e) {
      toast.error('Could not open this conversation');
    }
  }, []);

  useEffect(() => {
    if (activeId) openThread(activeId);
    // openThread is stable; re-running on every render would refetch in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  useEffect(() => { messagesRef.current = messages; }, [messages]);

  // Arriving from a notification: bring the conversation into view rather than
  // leaving it below the fold. Only when this panel is a section on a longer
  // page — in the inbox it is already the page, and scrolling would be wrong.
  // Once per mount, so a poll or a sent message never yanks the page.
  useEffect(() => {
    if (fill || broughtIntoView.current || loading) return;
    if (typeof window === 'undefined' || window.location.hash !== `#${SECTION_ID}`) return;
    // Wait for the conversation to render before scrolling. Firing as soon as
    // the thread list loaded moved the page, then the messages arrived beneath
    // and pushed the panel back down the viewport — it landed mid-screen
    // instead of at the top.
    const contentReady = !activeId || messages.length > 0;
    if (!contentReady) return;
    broughtIntoView.current = true;
    panelRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, [fill, loading, activeId, messages.length]);

  useEffect(() => {
    // Only follow the conversation if the reader was already at the bottom.
    // Yanking someone who scrolled up to re-read is worse than not scrolling.
    //
    // Sets scrollTop directly rather than calling scrollIntoView on a sentinel:
    // scrollIntoView also scrolls every ANCESTOR scroll container, including the
    // window, so following the newest message dragged the whole page — which
    // fought the notification scroll and landed the panel mid-viewport.
    if (!wasAtBottom.current) return;
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const noteScrollPosition = () => {
    const el = listRef.current;
    if (!el) return;
    wasAtBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight <= AT_BOTTOM_PX;
  };

  // Keep an open conversation live.
  useEffect(() => {
    if (!activeId) return undefined;

    const tick = async () => {
      if (document.hidden) return;
      try {
        const data = await apiGet(`/messages/threads/${activeId}`);
        const incoming = Array.isArray(data.messages) ? data.messages : [];
        // Compared against a ref, not inside a setState updater: React schedules
        // that function rather than running it, so a flag set inside it is still
        // false on the line after — which silently skipped both effects below.
        const prev = messagesRef.current;
        const lastId = (arr) => arr[arr.length - 1]?.message_id;
        const arrived = prev.length !== incoming.length || lastId(prev) !== lastId(incoming);
        if (arrived) {
          setMessages(incoming);
          // They are looking at the thread, so it is read. Without this the
          // bell would keep counting a conversation open on screen.
          apiPost(`/messages/threads/${activeId}/read`, {}).catch(() => {});
          onActivity?.();
        }
      } catch {
        // A failed poll is not worth a toast; the next tick recovers.
      }
    };

    const timer = setInterval(tick, POLL_MS);
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [activeId, onActivity]);

  const handleStart = async () => {
    setStarting(true);
    try {
      const thread = await apiPost('/messages/threads', { application_id: applicationId });
      await fetchThreads();
      setActiveId(thread.thread_id);
    } catch (e) {
      toast.error(e.message || 'Could not start a conversation');
    } finally {
      setStarting(false);
    }
  };

  const handleSend = async (e) => {
    e?.preventDefault();
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      const message = await apiPost(`/messages/threads/${activeId}/messages`, { body });
      wasAtBottom.current = true;   // you always follow your own message
      setMessages((prev) => [...prev, message]);
      setDraft('');
      fetchThreads();
      onActivity?.();
    } catch (err) {
      toast.error(err.message || 'Message not sent');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div ref={panelRef} id={SECTION_ID} className={styles.empty} data-testid="messages-loading">
        Loading conversations…
      </div>
    );
  }

  if (myThreads.length === 0) {
    return (
      <div ref={panelRef} id={SECTION_ID} className={styles.empty} data-testid="messages-empty">
        <MessageSquare size={22} aria-hidden="true" />
        <p className={styles.emptyTitle}>No conversations yet</p>
        {canStartThread ? (
          <>
            <p className={styles.emptyText}>
              Ask the client and their legal team a question about this application.
            </p>
            <button
              className="mcl-btn mcl-btn-primary mcl-btn-sm"
              onClick={handleStart}
              disabled={starting}
              data-testid="start-conversation-btn"
            >
              {starting ? 'Starting…' : 'Start a conversation'}
            </button>
          </>
        ) : (
          <p className={styles.emptyText}>
            When a funder has a question about this application, it will appear here.
          </p>
        )}
      </div>
    );
  }

  const active = myThreads.find((t) => t.thread_id === activeId);

  // "Dana Reyes · Auto accident · CO" — enough to know which case this is
  // without leaving the conversation.
  const caseOnlyLine = [
    caseInfo?.caseType ? getCaseTypeLabel(caseInfo.caseType) : '',
    caseInfo?.accidentState,
  ].filter(Boolean).join(' · ');

  const caseLine = [
    [caseInfo?.firstName, caseInfo?.lastName].filter(Boolean).join(' '),
    // Guarded: getCaseTypeLabel returns the literal "Not specified" for an
    // absent value, which is truthy and would render as if it were the case
    // type. An application without one should contribute nothing here.
    caseInfo?.caseType ? getCaseTypeLabel(caseInfo.caseType) : '',
    caseInfo?.accidentState,
  ].filter(Boolean).join(' · ');

  const roster = [...participants].sort(byRosterPriority);

  // Lead with whoever the viewer is not — the same rule the notification bell
  // and the inbox rows follow. A funder is one party in every thread they can
  // see, so titling the header with funder_company printed their own company
  // back at them; the client is the party that varies.
  const viewerIsFunder = currentUser?.role === 'funder';
  const clientName = [caseInfo?.firstName, caseInfo?.lastName].filter(Boolean).join(' ');
  const headerTitle = viewerIsFunder
    ? (clientName || active?.funder_company || 'Conversation')
    : (active?.funder_company || 'Conversation');
  // Don't repeat the title in the line beneath it.
  const headerSubtitle = viewerIsFunder ? caseOnlyLine : caseLine;

  return (
    <div
      ref={panelRef}
      id={SECTION_ID}
      className={`${styles.panel} ${fill ? styles.panelFill : ''}`}
      data-testid="messages-panel"
      data-fill={fill ? 'true' : 'false'}
    >
      {myThreads.length > 1 && !initialThreadId && (
        <div className={styles.threadList} data-testid="thread-list">
          {myThreads.map((t) => (
            <button
              key={t.thread_id}
              className={`${styles.threadItem} ${t.thread_id === activeId ? styles.threadActive : ''}`}
              onClick={() => setActiveId(t.thread_id)}
              data-testid={`thread-${t.thread_id}`}
              data-unread={t.unread ? 'true' : 'false'}
            >
              <span className={styles.threadName}>{t.funder_company || 'Funder'}</span>
              {t.unread && <span className={styles.unreadDot} data-testid="thread-unread-dot" aria-label="Unread" />}
              <span className={styles.threadPreview}>{t.last_message_preview || 'No messages yet'}</span>
            </button>
          ))}
        </div>
      )}

      <div className={styles.conversation}>
        {/* States the room rather than implying it. This is not decoration: a
            funder is talking to a represented plaintiff, and the attorney's
            presence is the safeguard that makes that acceptable — so it should
            be visible to everyone in the thread. */}
        <div className={styles.threadHead} data-testid="thread-header">
          <div className={styles.threadWho}>
            <strong data-testid="thread-title">{headerTitle}</strong>
            {headerSubtitle && <span data-testid="thread-subtitle">{headerSubtitle}</span>}
          </div>
          {participants.length > 0 && (
            <div className={styles.roster} data-testid="thread-roster">
              {/* Capped: a firm with a large support team overflowed the header
                  with near-identical chips. The client, the funder and the
                  attorney are the ones that carry meaning; the rest collapse. */}
              {roster.slice(0, ROSTER_VISIBLE).map((p) => (
                <span
                  key={p.user_id}
                  className={styles.rosterChip}
                  data-role={p.role}
                  data-testid={`participant-${p.user_id}`}
                >
                  {p.name}
                  {ROLE_LABEL[p.role] && p.role !== 'funder' && ` · ${ROLE_LABEL[p.role]}`}
                </span>
              ))}
              {roster.length > ROSTER_VISIBLE && (
                <span
                  className={styles.rosterChip}
                  data-testid="thread-roster-more"
                  title={roster.slice(ROSTER_VISIBLE).map((p) => p.name).join(', ')}
                >
                  +{roster.length - ROSTER_VISIBLE} more
                </span>
              )}
            </div>
          )}
        </div>

        <div className={styles.messages} data-testid="message-list" ref={listRef} onScroll={noteScrollPosition}>
          {messages.length === 0 && (
            <p className={styles.emptyText} data-testid="no-messages">No messages yet — say hello.</p>
          )}
          {messages.map((m) => {
            const mine = m.sender_id === currentUser?.user_id;
            return (
              <div
                key={m.message_id}
                className={styles.message}
                data-testid={`message-${m.message_id}`}
                data-role={m.sender_role}
                data-mine={mine ? 'true' : 'false'}
              >
                <span className={styles.avatar} data-role={m.sender_role} aria-hidden="true">
                  {initials(m.sender_name)}
                </span>
                <div className={styles.messageBody}>
                  <div className={styles.byline}>
                    <span className={styles.sender}>{m.sender_name}</span>
                    <span className={styles.roleChip} data-role={m.sender_role}>
                      {ROLE_LABEL[m.sender_role] || m.sender_role}
                    </span>
                    <span className={styles.stamp} data-testid="message-stamp">
                      {formatDateTime(m.created_at)}
                    </span>
                  </div>
                  <div className={styles.bubble}>{m.body}</div>
                </div>
              </div>
            );
          })}
        </div>

        <form className={styles.composer} onSubmit={handleSend} data-testid="message-composer">
          <input
            className={styles.field}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Write a message…"
            maxLength={5000}
            disabled={!active}
            data-testid="message-input"
            aria-label="Write a message"
          />
          <button
            type="submit"
            className="mcl-btn mcl-btn-primary mcl-btn-sm"
            disabled={sending || !draft.trim()}
            data-testid="message-send-btn"
          >
            <Send size={14} /> {sending ? 'Sending…' : 'Send'}
          </button>
        </form>

        <p className={styles.notice} data-testid="messages-notice">
          <Lock size={11} aria-hidden="true" />
          {PRIVACY_NOTICE[currentUser?.role] || DEFAULT_PRIVACY_NOTICE}
        </p>
      </div>
    </div>
  );
};

export default MessagesPanel;
